import { Skeleton } from './Skeleton';

/**
 * Loading placeholder for the AdminPage dashboard (5 stat cards + upcoming +
 * recent sections).
 */
export function AdminDashboardSkeleton() {
  return (
    <section className="max-w-editorial mx-auto px-6 sm:px-10 py-12" aria-hidden="true">
      <div className="mb-10">
        <Skeleton className="h-10 w-64 mb-3" />
        <Skeleton className="h-4 w-96" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="border border-linen p-5">
            <Skeleton className="h-3 w-20 mb-3" />
            <Skeleton className="h-10 w-16" />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i}>
            <Skeleton className="h-5 w-48 mb-5" />
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, j) => (
                <div key={j} className="flex items-center gap-4 py-3 border-b border-linen">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-20 ms-auto" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * Generic table skeleton used by both AdminAppointments and AdminProducts.
 */
export function AdminTableSkeleton({ rows = 8, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <section className="max-w-editorial mx-auto px-6 sm:px-10 py-12" aria-hidden="true">
      <div className="mb-8">
        <Skeleton className="h-10 w-56 mb-3" />
        <Skeleton className="h-4 w-80" />
      </div>

      {/* Filters / search bar */}
      <div className="flex flex-wrap gap-4 mb-8 pb-6 border-b border-linen">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-10 w-40" />
      </div>

      {/* Header row */}
      <div className="grid gap-4 pb-3 border-b border-linen mb-2"
           style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} className="h-3 w-20" />
        ))}
      </div>

      {/* Body rows */}
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="grid gap-4 py-4 border-b border-linen"
               style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
            {Array.from({ length: columns }).map((_, c) => (
              <Skeleton key={c} className={`h-4 ${c === 0 ? 'w-32' : 'w-24'}`} />
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * Loading placeholder for the AdminCalendar month grid.
 */
export function AdminCalendarSkeleton() {
  return (
    <section className="max-w-editorial mx-auto px-6 sm:px-10 py-12" aria-hidden="true">
      <div className="flex items-center justify-between mb-8">
        <div>
          <Skeleton className="h-10 w-48 mb-3" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-9 rounded-full" />
          <Skeleton className="h-9 w-36" />
          <Skeleton className="h-9 w-9 rounded-full" />
        </div>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-px bg-linen border border-linen mb-2">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="bg-ivory p-2">
            <Skeleton className="h-3 w-8" />
          </div>
        ))}
      </div>

      {/* 6 weeks × 7 days */}
      <div className="grid grid-cols-7 gap-px bg-linen border border-linen">
        {Array.from({ length: 42 }).map((_, i) => (
          <div key={i} className="bg-ivory aspect-square p-2 flex flex-col gap-2">
            <Skeleton className="h-3 w-4" />
            <Skeleton className="h-2 w-full" />
            <Skeleton className="h-2 w-3/4" />
          </div>
        ))}
      </div>
    </section>
  );
}
